import type { ValueOf } from './util/typeUtils';

export const MESSAGING_QUERY_KEY = 'pl';

export const MEASURING_UNITS = {
    SQUARE_METERS: 'm²',
} as const;

export type MEASURING_UNIT = ValueOf<typeof MEASURING_UNITS>;

export const S3_BUCKETS = {
    ATTACHMENTS: 'attachments',
    DOCUMENTS: 'documents',
    OFFERS: 'offers',
    OFFERS_ERROR_SNAPSHOT: 'offers-error-snapshots',
    WHATSAPP_INBOUND: 'whatsapp-inbound',
    WHATSAPP_OUTBOUND: 'whatsapp-outbound',
} as const;

export type S3_BUCKET = ValueOf<typeof S3_BUCKETS>;

export const LEAD_DETAILS_FORMS = {
    EMPLOYMENT_APPLICANT1: 'EMPLOYMENT_APPLICANT1',
    EMPLOYMENT_APPLICANT2: 'EMPLOYMENT_APPLICANT2',
    EXPENSES: 'EXPENSES',
    INCOME: 'INCOME',
    LIVING_SITUATION: 'LIVING_SITUATION',
    LOANS: 'LOANS',
    PERSONAL_DATA: 'PERSONAL_DATA',
    REAL_ESTATE: 'REAL_ESTATE',
} as const;
export type LEAD_DETAILS_FORM = keyof typeof LEAD_DETAILS_FORMS;

export const OVERVIEW_TABS = {
    BANK_REQUEST: 'BANK_REQUEST',
    CUSTOMER_DATA: 'CUSTOMER_DATA',
    DOCUMENT_CENTER: 'DOCUMENT_CENTER',
    HISTORY: 'HISTORY',
    OFFER_MANAGEMENT: 'OFFER_MANAGEMENT',
} as const;

export type OVERVIEW_TAB = keyof typeof OVERVIEW_TABS;

export const CUSTOMER_DATA_TABS = {
    EMPLOYMENT: 'EMPLOYMENT',
    INCOME_AND_EXPENSES: 'INCOME_AND_EXPENSES',
    LIVING_SITUATION: 'LIVING_SITUATION',
    PERSONAL_DATA: 'PERSONAL_DATA',
    REAL_ESTATE: 'REAL_ESTATE',
} as const;
export type CUSTOMER_DATA_TAB = ValueOf<typeof CUSTOMER_DATA_TABS>;

export const CUSTOMER_DATA_NOTIFICATIONS = {
    EMPLOYMENT: {} as const,
    INCOME_AND_EXPENSES: {} as const,
    LIVING_SITUATION: {
        HAS_NO_SELF_OCCUPIED_REAL_ESTATE: 'HAS_NO_SELF_OCCUPIED_REAL_ESTATE',
        SHOULD_BE_LIVING_IN_OWN_HOUSE: 'SHOULD_BE_LIVING_IN_OWN_HOUSE',
    } as const,
    PERSONAL_DATA: {} as const,
    REAL_ESTATE: {} as const,
} as const;
export type CUSTOMER_DATA_NOTIFICATION = keyof typeof CUSTOMER_DATA_NOTIFICATIONS;

export const LEAD_ERROR_RESPONSE_MESSAGES = {
    LEAD_NOT_FOUND: 'LEAD_NOT_FOUND',
} as const;

export const USER_ERROR_RESPONSE_MESSAGES = {
    USER_NOT_FOUND: 'USER_NOT_FOUND',
} as const;

export const MAX_FILE_SIZE = 52428800;
/** Client-side cap on files per upload session */
export const MAX_UPLOAD_FILE_COUNT = 5;
// Abort a file upload when no upload progress arrives for this long
export const UPLOAD_IDLE_TIMEOUT_MS = 60_000;
export const HTTP_STATUS = {
    BAD_REQUEST: 400,
    FORBIDDEN: 403,
    NOT_FOUND: 404,
    OK: 200,
    REQUEST_TIMEOUT: 408,
    SERVER_ERROR: 500,
    UNAUTHORIZED: 401,
} as const;

// Multipart form field prefix used by the applicant document upload to address a demanded
// document by its (URL-encoded) name instead of by list index — indexes shift whenever a
// document is provided between verify and upload (e.g. a second upload round in one session).
export const DOCUMENT_UPLOAD_NAME_FIELD_PREFIX = 'name:';
export const ALLOWED_FILE_TYPES_KEYS = {
    CSV: 'text/csv',
    DOCX: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    JPEG: 'image/jpeg',
    JPG: 'image/jpg',
    PDF: 'application/pdf',
    PNG: 'image/png',
    PPT: 'application/vnd.ms-powerpoint',
    PPTX: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    TXT: 'text/plain',
    WORD: 'application/msword',
    XLS: 'application/vnd.ms-excel',
    XLSX: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as const;
export const ALLOWED_FILE_TYPES = Object.values(ALLOWED_FILE_TYPES_KEYS);

export const DETECTED_FILE_KINDS = {
    CFB: 'cfb',
    JPEG: 'jpeg',
    PDF: 'pdf',
    PNG: 'png',
    TEXT: 'text',
    ZIP_OOXML: 'zip-ooxml',
} as const;

export const ALLOWED_FILE_KIND_RULES = {
    [DETECTED_FILE_KINDS.CFB]: {
        extensions: ['doc', 'xls', 'ppt'],
        mimeTypes: [ALLOWED_FILE_TYPES_KEYS.WORD, ALLOWED_FILE_TYPES_KEYS.XLS, ALLOWED_FILE_TYPES_KEYS.PPT],
    },
    [DETECTED_FILE_KINDS.JPEG]: {
        extensions: ['jpg', 'jpeg'],
        mimeTypes: [ALLOWED_FILE_TYPES_KEYS.JPEG, ALLOWED_FILE_TYPES_KEYS.JPG],
    },
    [DETECTED_FILE_KINDS.PDF]: { extensions: ['pdf'], mimeTypes: [ALLOWED_FILE_TYPES_KEYS.PDF] },
    [DETECTED_FILE_KINDS.PNG]: { extensions: ['png'], mimeTypes: [ALLOWED_FILE_TYPES_KEYS.PNG] },
    [DETECTED_FILE_KINDS.TEXT]: {
        extensions: ['txt', 'csv'],
        mimeTypes: [ALLOWED_FILE_TYPES_KEYS.TXT, ALLOWED_FILE_TYPES_KEYS.CSV],
    },
    [DETECTED_FILE_KINDS.ZIP_OOXML]: {
        extensions: ['docx', 'xlsx', 'pptx'],
        mimeTypes: [ALLOWED_FILE_TYPES_KEYS.DOCX, ALLOWED_FILE_TYPES_KEYS.XLSX, ALLOWED_FILE_TYPES_KEYS.PPTX],
    },
} as const;

// Leading bytes read from an upload to detect its real type
export const FILE_SNIFF_BYTES = 4096;

export const FILE_MAGIC_BYTES = {
    [DETECTED_FILE_KINDS.CFB]: [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1],
    [DETECTED_FILE_KINDS.JPEG]: [0xff, 0xd8, 0xff],
    [DETECTED_FILE_KINDS.PDF]: [0x25, 0x50, 0x44, 0x46, 0x2d],
    [DETECTED_FILE_KINDS.PNG]: [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a],
    [DETECTED_FILE_KINDS.ZIP_OOXML]: [0x50, 0x4b, 0x03, 0x04],
} as const;

// Entry every OOXML (docx/xlsx/pptx) zip container carries
export const OOXML_CONTENT_TYPES_ENTRY = '[Content_Types].xml';

export const CPOOL_APPS = {
    APPLICANTS: 'applicants',
    BACKEND: 'backend',
    COBRA: 'cobra',
    CRAP: 'crap',
    EULE: 'eule',
    FRONTEND: 'frontend',
    MANTA: 'manta',
    MARKETING: 'marketing',
    PUMA: 'puma',
    RAVEN: 'raven',
    SLOTH: 'sloth',
} as const;

export type CPOOL_APP = ValueOf<typeof CPOOL_APPS>;

export const AGGREGATE_NAMES = {
    CHAT: 'chat',
    CHAT_READ_CURSOR: 'chat-read-cursor',
    CORPORATION: 'corporation',
    DIVISION: 'division',
    LOAN_REQUEST: 'loan-request',
    PARTNER: 'partner',
    SYSTEM_VARIABLE: 'system-variable',
    TEAM: 'team',
    TEMPLATE: 'template',
    USER: 'user',
    WABA: 'waba',
} as const;

export const BACKEND_RESOURCE_NAMES = {
    API: 'api',
    COMMANDS: 'commands',
    RESOLVERS: 'resolvers',
} as const;

// WhatsApp media message types — originally just inbound (Meta webhook `messages[].type` values
// that carry an attachment, as opposed to `text`), now shared with outbound sends too
// (Chat_MessageQueued's `media` variant / MetaWhatsAppService.sendMedia). `STICKER` stays
// inbound-only: sending one requires a specific pre-registered webp sticker format, never derived
// from an arbitrary uploaded file.
export const CHAT_MEDIA_TYPES = {
    AUDIO: 'audio',
    DOCUMENT: 'document',
    IMAGE: 'image',
    STICKER: 'sticker',
    VIDEO: 'video',
} as const;

export type ChatMediaType = ValueOf<typeof CHAT_MEDIA_TYPES>;

// Per-media-type upload limits mirroring Meta's WhatsApp Cloud API media size limits (not an
// arbitrary internal choice) - the generic `MAX_FILE_SIZE` (meant for loan documents) is far too
// permissive for chat attachments, letting an upload succeed here only to be rejected by Meta's
// Graph API later. STICKER is inbound-only (see `CHAT_MEDIA_TYPES` comment) so it has no outbound
// upload limit to enforce.
export const CHAT_MEDIA_SIZE_LIMITS: Record<Exclude<ChatMediaType, 'sticker'>, number> = {
    [CHAT_MEDIA_TYPES.AUDIO]: 16 * 1024 * 1024,
    [CHAT_MEDIA_TYPES.DOCUMENT]: 100 * 1024 * 1024,
    [CHAT_MEDIA_TYPES.IMAGE]: 5 * 1024 * 1024,
    [CHAT_MEDIA_TYPES.VIDEO]: 16 * 1024 * 1024,
};

export type BACKEND_RESOURCE_NAME = ValueOf<typeof BACKEND_RESOURCE_NAMES>;

export type AGGREGATE_NAME = ValueOf<typeof AGGREGATE_NAMES>;

// AWS GuardDuty Malware Protection for S3 writes this object tag once it finishes scanning an
// uploaded object — see `pollGuardDutyScanStatus` (cpool-utils-node) for the bounded-retry polling
// loop that reads it. Enabling the scan itself on the bucket is an AWS account-level/IaC
// prerequisite, not something any of this code does.
export const GUARDDUTY_MALWARE_SCAN_TAG_KEY = 'GuardDutyMalwareScanStatus';

export const GUARDDUTY_MALWARE_SCAN_STATUSES = {
    ACCESS_DENIED: 'ACCESS_DENIED',
    FAILED: 'FAILED',
    NO_THREATS_FOUND: 'NO_THREATS_FOUND',
    THREATS_FOUND: 'THREATS_FOUND',
    UNSUPPORTED: 'UNSUPPORTED',
} as const;

export type GuardDutyMalwareScanStatus = ValueOf<typeof GUARDDUTY_MALWARE_SCAN_STATUSES>;

// Collapsed verdict from `pollGuardDutyScanStatus` (cpool-utils-node): only an explicit
// NO_THREATS_FOUND tag is SAFE; every other scan status or a timed-out poll is UNSAFE.
export const GUARDDUTY_SCAN_OUTCOMES = {
    SAFE: 'safe',
    UNSAFE: 'unsafe',
} as const;

export type GuardDutyScanOutcome = ValueOf<typeof GUARDDUTY_SCAN_OUTCOMES>;

// Ops-facing reason for a scan result — built only via `toGuardDutyScanResult` in cpool-utils-node.
export const GUARDDUTY_SCAN_REASONS = {
    ACCESS_DENIED: 'access-denied',
    INCONCLUSIVE: 'inconclusive',
    NO_THREATS: 'no-threats',
    TAGGING_ERROR: 'tagging-error',
    THREAT: 'threat',
    TIMEOUT: 'timeout',
} as const;

export type GuardDutyScanReason = ValueOf<typeof GUARDDUTY_SCAN_REASONS>;

export const RUNTIME_ENVIRONMENTS = {
    DEV: 'dev',
    LOCAL: 'local',
    PRODUCTION: 'production',
    STAGING: 'staging',
} as const;

export type RUNTIME_ENVIRONMENT = ValueOf<typeof RUNTIME_ENVIRONMENTS>;
